<script setup lang="ts">
/**
 * Waiting, said calmly and then honestly (SPEC §8.6.2, §22.1).
 *
 * The slowest entry to this product is also the most common: the first person
 * to open the link all week, on a Sunday morning, against a cold app instance
 * and possibly a resumed database. They arrived from a text message and have
 * no context for why nothing is happening.
 *
 * So there are three stages rather than one spinner:
 *
 * - immediately, a block the size of the thing being loaded, so nothing moves
 *   when the data arrives;
 * - at five seconds, reassurance, because by then somebody is wondering
 *   whether the link worked;
 * - at twenty seconds, a way out, because a spinner that never stops is a
 *   screen somebody closes.
 *
 * The blocks reserve height deliberately. A loader that occupies no space is a
 * loader that makes the page jump the moment it is replaced, which is the
 * layout shift SPEC §8.6.2 rules out.
 */
import { onUnmounted, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    /** How many placeholder lines to reserve. */
    lines?: number
    /** Named so the reassurance can say what is still coming. */
    label?: string
  }>(),
  { lines: 3, label: 'your family' },
)

const emit = defineEmits<{ retry: [] }>()

/** Where in the escalation we are. */
const stage = ref<'quiet' | 'reassuring' | 'stuck'>('quiet')

/**
 * Five and twenty seconds, from SPEC §8.6.2.
 *
 * Exported so the test can drive them rather than sit through them; a suite
 * that really waited twenty seconds is a suite somebody stops running.
 */
const REASSURE_MS = 5_000
const GIVE_UP_MS = 20_000

let timers: ReturnType<typeof setTimeout>[] = []

function start() {
  stop()

  stage.value = 'quiet'

  timers = [
    setTimeout(() => {
      if (stage.value === 'quiet') stage.value = 'reassuring'
    }, REASSURE_MS),
    setTimeout(() => {
      stage.value = 'stuck'
    }, GIVE_UP_MS),
  ]
}

function stop() {
  timers.forEach(clearTimeout)
  timers = []
}

start()

onUnmounted(stop)

function retry() {
  // The clock restarts, so a second slow attempt escalates the same way rather
  // than jumping straight back to the failure it was just showing.
  start()

  emit('retry')
}
</script>

<template>
  <section class="wait" aria-busy="true" data-testid="loading">
    <template v-if="stage !== 'stuck'">
      <!--
        Blocks rather than a spinner. A spinner on white says "something is
        happening somewhere"; a block the shape of the card says "your meal is
        about to be here" and holds the space it will take (SPEC §8.6.2).
      -->
      <div
        v-for="line in props.lines"
        :key="line"
        class="wait__block"
        :class="{ 'wait__block--short': line === props.lines }"
      />

      <p v-if="stage === 'reassuring'" class="wait__note" data-testid="loading-reassurance">
        Still loading — hang tight.
      </p>
    </template>

    <div v-else class="wait__stuck" data-testid="loading-stuck">
      <p class="wait__stuck-text">
        This is taking longer than it should. Your connection may have dropped.
      </p>
      <v-btn class="wait__retry" color="primary" variant="flat" @click="retry"> Try again </v-btn>
    </div>

    <!--
      One live region for all three stages, so a screen reader is told that
      something is loading, then that it is still loading, then that it failed
      — rather than being told nothing at all (SPEC §9.5).
    -->
    <p aria-live="polite" class="wait__announcement">
      <template v-if="stage === 'quiet'">Loading {{ props.label }}.</template>
      <template v-else-if="stage === 'reassuring'">Still loading {{ props.label }}.</template>
      <template v-else>{{ props.label }} did not load. A retry is available.</template>
    </p>
  </section>
</template>

<style scoped>
.wait {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/*
  Fixed heights, so the space is reserved rather than collapsed. This is the
  whole reason these are blocks and not a spinner.
*/
.wait__block {
  height: 1.25rem;
  border-radius: 0.375rem;
  background: rgb(var(--v-theme-surface-sunken, 242 236 227));
  animation: wait-pulse 1.8s ease-in-out infinite;
}

.wait__block:first-child {
  height: 2.25rem;
}

.wait__block--short {
  width: 60%;
}

/* Calm rather than urgent, and gone entirely for anybody who asked for that. */
@keyframes wait-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wait__block {
    animation: none;
  }
}

.wait__note,
.wait__stuck-text {
  margin: 0;
  color: rgb(var(--v-theme-on-surface-variant));
}

.wait__stuck {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
}

/* Thumb-sized, like every other target in this product (SPEC §9.5). */
.wait__retry {
  min-height: 2.75rem;
}

.wait__announcement {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
